import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./CustomTooltip.module.scss";

const CustomTooltip = ({ text, description, position = "top" }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
	const triggerRef = useRef(null);
	const tooltipRef = useRef(null);
	const timeoutRef = useRef(null);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const calculatePosition = useCallback(() => {
		if (!triggerRef.current || !tooltipRef.current) return;

		const triggerRect = triggerRef.current.getBoundingClientRect();
		const tooltipRect = tooltipRef.current.getBoundingClientRect();

		let top = 0;
		let left = 0;

		switch (position) {
			case "top":
				top = triggerRect.top - tooltipRect.height - 8;
				left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
				break;
			case "bottom":
				top = triggerRect.bottom + 8;
				left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
				break;
			case "left":
				top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
				left = triggerRect.left - tooltipRect.width - 8;
				break;
			case "right":
				top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
				left = triggerRect.right + 8;
				break;
			default:
				break;
		}

		const padding = 10;
		if (left < padding) left = padding;
		if (left + tooltipRect.width > window.innerWidth - padding) {
			left = window.innerWidth - tooltipRect.width - padding;
		}
		if (top < padding) top = padding;

		setTooltipPosition({ top, left });
	}, [position]);

	const handleMouseEnter = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setIsVisible(true);
		}, 200);
	};

	const handleMouseLeave = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setIsVisible(false);
		}, 100);
	};

	useEffect(() => {
		if (isVisible) {
			calculatePosition();
		}
	}, [isVisible, calculatePosition]);

	return (
		<>
			<span ref={triggerRef} className={styles.trigger} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
				{text}
			</span>

			{isVisible && (
				<div
					ref={tooltipRef}
					className={`${styles.tooltip} ${styles[position]}`}
					style={{
						top: `${tooltipPosition.top}px`,
						left: `${tooltipPosition.left}px`,
					}}
				>
					{description}
					<div className={styles.arrow}></div>
				</div>
			)}
		</>
	);
};

export default CustomTooltip;
